/**
 * CritiqueModeBody - Main content area for critique mode
 * Feature: Refactoring CollectionCritiqueModePage
 *
 * Contains the split panel layout with:
 * - Left panel: Collection + Detail view
 * - Right panel: Critique comments
 */

import { useState, useMemo, useEffect } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { CollectionAndDetailView2 } from '@/components/CollectionAndDetailView2/CollectionAndDetailView2';
import { CritiqueComments } from '@/components/composed/CritiqueComments/CritiqueComments';
import { CritiqueModeProvider } from '@/contexts/CritiqueModeContext';
import type { PhotoItem2ViewModel } from '@/components/PhotoItem2/PhotoItem2.types';
import type { ViewMode } from '@/contexts/ViewModeContext';
import type { ZoomLevel } from '@/hooks/useZoomPan';
import type { LayoutMode } from '../CritiqueModeFooterControls/CritiqueModeFooterControls';
import type { Collection } from '@/graphql/types';
import type { CritiqueSessionData } from '@/hooks/useCritiqueModeHandlers';
import type { PhotoVersion } from '@/hooks/graphql/usePhotoVersionsGraphQL';

interface ViewModeConfiguration {
  displayName: string;
  collectionPosition: 'left' | 'top' | 'bottom' | 'right';
  layout: 'vertical' | 'horizontal' | 'grid' | 'magazine';
  itemTemplate: 'classic-feed' | 'photo-only' | 'full-details' | 'lightroom-list';
  hideDetail: boolean;
  zoom: number;
  defaultCollectionSize?: number;
}

interface CritiqueModeBodyProps {
  // Photo data
  photos: PhotoItem2ViewModel[];
  selectedPhoto: PhotoItem2ViewModel | undefined;

  // Layout configuration
  layoutMode: LayoutMode;
  viewModeConfig: ViewModeConfiguration;
  viewMode: ViewMode;

  // URL state
  urlState: {
    photoId: string | null;
    commentId: string | null;
    errors: {
      invalidPhotoFormat?: boolean;
      invalidCommentFormat?: boolean;
      photoNotFound?: boolean;
      commentNotFound?: boolean;
      commentPhotoMismatch?: boolean;
    };
  };

  // Selection state
  selectedIds: string[];

  // Session data
  session: CritiqueSessionData | null | undefined;
  collection: Collection;

  // Reply context
  replyingTo: string | null;
  setReplyingTo: (value: string | null) => void;

  // Zoom state
  sessionZoomLevel: ZoomLevel;
  setSessionZoomLevel: (level: ZoomLevel) => void;

  // Permissions
  isSessionOwner: boolean | null | undefined;
  /** Whether current user is the collection owner (photographer) */
  isCollectionOwner: boolean | null | undefined;

  // Event handlers
  onPhotoSelect: (photoId: string) => void;
  onCritiqueClick: (photoId: string, commentId: string) => void;
  onCommentClick: (commentId: string) => void;
  onCommentSubmitted: (photoId: string, commentId: string) => void;
}

export function CritiqueModeBody({
  photos,
  selectedPhoto,
  layoutMode,
  viewModeConfig,
  viewMode,
  urlState,
  selectedIds,
  session,
  collection,
  replyingTo,
  setReplyingTo,
  sessionZoomLevel,
  setSessionZoomLevel,
  isSessionOwner,
  isCollectionOwner,
  onPhotoSelect,
  onCritiqueClick,
  onCommentClick,
  onCommentSubmitted,
}: CritiqueModeBodyProps) {
  // Feature 649: Determine if rating feedback is enabled for this session
  const hasRatingFeedback = session?.feedbackOptions?.hasRating ?? false;

  // Feature 081: Track selected version for version switching
  // Only set when user explicitly selects a non-current version
  const [selectedVersion, setSelectedVersion] = useState<PhotoVersion | null>(null);

  // Feature 081: Current photo ID for version reset effect
  const currentPhotoId = selectedIds[0];

  // Feature 081: Handle version selection
  const handleVersionSelect = (version: PhotoVersion) => {
    // If selecting the current version, clear the override (show original photo)
    if (version.isCurrent) {
      setSelectedVersion(null);
    } else {
      setSelectedVersion(version);
    }
  };

  // Feature 081: Reset selected version when photo changes
  useEffect(() => {
    setSelectedVersion(null);
  }, [currentPhotoId]);

  // Feature 081: Create modified photo with version's variants
  // Only applies when a non-current version is explicitly selected
  const displayPhoto = useMemo(() => {
    if (!selectedPhoto) {
      return selectedPhoto;
    }

    // If no version explicitly selected, use the original photo unchanged
    // The original photo already has correct variants from the API
    if (!selectedVersion) {
      return selectedPhoto;
    }

    // Use the selected version's variants (from GraphQL query)
    const versionVariants = selectedVersion.variants;

    // If version has no variants, fall back to original photo
    if (!versionVariants || versionVariants.length === 0) {
      return selectedPhoto;
    }

    // IMPORTANT: publicUrl is based on photo ID, not version ID, so all versions
    // share the same publicUrl which always serves the current version's image.
    // To display a non-current version, we must use storageUrl instead.
    // We do this by setting publicUrl to storageUrl in the variant objects.
    const variantsWithStorageUrls = versionVariants.map(v => ({
      ...v,
      publicUrl: v.storageUrl, // Force AdaptivePhoto to use version-specific URL
    }));

    // Return modified photo with version's variants (using storageUrl as publicUrl)
    return {
      ...selectedPhoto,
      photoData: {
        ...selectedPhoto.photoData,
        variants: variantsWithStorageUrls,
        width: selectedVersion.width || selectedPhoto.photoData.width,
        height: selectedVersion.height || selectedPhoto.photoData.height,
      },
    };
  }, [selectedPhoto, selectedVersion]);

  // Feature 081: Create modified photos array for detail view
  // Replace the selected photo with the display photo when viewing a different version
  const displayPhotos = useMemo(() => {
    // Only replace when a non-current version is selected
    if (!selectedVersion || !displayPhoto) {
      return photos;
    }

    return photos.map((photo) => {
      if (photo.photoData.id === currentPhotoId) {
        return displayPhoto;
      }
      return photo;
    });
  }, [photos, selectedVersion, displayPhoto, currentPhotoId]);

  return (
    <CritiqueModeProvider
      sessionId={session?.globalId}
      isPhotographer={!!isCollectionOwner}
      hasRatingFeedback={hasRatingFeedback}
    >
      <main className="flex-1 min-h-0">
        <PanelGroup direction="horizontal" className="h-full">
          {/* Left panel: Collection + Detail view */}
        <Panel defaultSize={80} minSize={20}>
          {/* T014: Error handling UI (040-critique-url-state) */}
          {urlState.errors.photoNotFound && (
            <div className="bg-danger bg-opacity-10 border-l-4 border-danger p-4 mb-4">
              <p className="text-danger font-medium">Photo not found in this collection</p>
              <p className="text-sm text-muted mt-1">
                The photo ID in the URL doesn't exist. Showing first photo instead.
              </p>
            </div>
          )}
          <CollectionAndDetailView2
            photos={displayPhotos}
            collectionPosition={viewModeConfig.collectionPosition}
            layout={viewModeConfig.layout}
            itemTemplate={viewModeConfig.itemTemplate}
            hideDetail={viewModeConfig.hideDetail}
            zoom={viewModeConfig.zoom}
            collectionDefaultSize={viewModeConfig.defaultCollectionSize}
            autoSaveId={`critique-view-${layoutMode}`}
            selectionMode="always-one" // Exactly one photo always selected
            selectedIds={selectedIds}
            onSelectionChange={(ids) => {
              // T013: Update URL when user clicks a photo (040-critique-url-state)
              if (ids.length > 0) {
                onPhotoSelect(ids[0]);
              }
            }}
            onCritiqueClick={onCritiqueClick}
            initialZoomLevel={sessionZoomLevel}
            onZoomChange={setSessionZoomLevel}
            className="h-full"
          />
        </Panel>

        {/* Resizer handle */}
        {viewMode != 'fullscreen' &&
        <PanelResizeHandle className="w-1 bg-border hover:bg-primary transition-colors" />}

        {/* Right panel: Critique comments */}
        {viewMode != 'fullscreen' &&
        <Panel defaultSize={20} minSize={20}>
          {selectedIds[0] && (
            <div className="h-full overflow-y-auto bg-surface-1 p-4">
              <CritiqueComments
                photoId={selectedIds[0]}
                collectionId={collection.globalId!}
                sessionId={session?.globalId} // Feature 056 US2: Filter comments by session
                selectedCritiqueId={urlState.commentId} // T025: Pass selectedCritiqueId for highlighting (040-critique-url-state)
                onCommentClick={onCommentClick} // T039: Pass comment click handler (040-critique-url-state)
                photoData={selectedPhoto ? {
                  description: selectedPhoto.photoData.description || undefined,
                  exif: selectedPhoto.photoData.metadata || undefined
                } : undefined}
                photo={displayPhoto?.photoData} // Pass full photo for edit/markup modals (version-aware)
                replyingTo={replyingTo} // Pass reply context to persist across modals
                onReplyChange={setReplyingTo} // Allow CritiqueComments to update reply context
                onCommentSubmitted={onCommentSubmitted} // Navigate to comment after submission
                isAiSession={session?.isAiSession} // Feature 071: AI session view-only mode
                isOwner={!!isSessionOwner} // Feature 071: Owners can post in AI sessions
                feedbackOptions={session?.feedbackOptions ? { enabled: session.feedbackOptions.enabled as ('general' | 'rating')[] } : undefined} // Feature 649: Rating feedback options
                isPhotographer={!!isCollectionOwner} // Feature 649: Collection owner is the photographer
                onVersionSelect={handleVersionSelect} // Feature 081: Version selection callback
                selectedVersionId={selectedVersion?.id} // Feature 081: Currently selected version
              />
            </div>
          )}
        </Panel>
        }
        </PanelGroup>
      </main>
    </CritiqueModeProvider>
  );
}
